import { cn } from "@/lib/utils";
import { type ComponentPropsWithoutRef, type ElementType } from "react";
import { tv, type VariantProps } from "tailwind-variants";

const textVariants = tv({
  base: "font-sans text-foreground",
  variants: {
    variant: {
      heading: "",
      title: "",
      label: "",
      body: "",
      inherit: "text-inherit leading-inherit", // Inherits size and line-height
    },
    size: {
      lg: "",
      md: "",
      sm: "",
    },
    weight: {
      regular: "font-normal", // 400
      medium: "font-medium", // 500
      semibold: "font-semibold", // 600
      inherit: "font-inherit", // Inherits font-weight
    },
  },
  compoundVariants: [
    // Heading variants
    {
      variant: "heading",
      size: "lg",
      class:
        "text-[1.75rem] leading-[2.25rem] md:text-[2rem] md:leading-[2.5rem]", // 32px / 40px
    },
    {
      variant: "heading",
      size: "md",
      class: "text-2xl leading-8 md:text-[1.75rem] leading-[2.25rem]", // 28px / 36px
    },
    {
      variant: "heading",
      size: "sm",
      class: "text-2xl leading-8", // 24px / 32px
    },
    // Title variants
    {
      variant: "title",
      size: "lg",
      class:
        "text-lg md:text-[1.375rem] 4xl:text-[1.5rem] leading-[1.75rem] 4xl:leading-[2rem]", // 22px / 28px / 24px / 32px
    },
    {
      variant: "title",
      size: "md",
      class:
        "text-base 4xl:text-lg leading-6 4xl:leading-7 tracking-[0.0015em]", // 16px / 24px / 18px / 28px / +0.15%
    },
    {
      variant: "title",
      size: "sm",
      class: "text-sm leading-5 tracking-[0.0001em]", // 14px / 20px / +0.1%
    },
    // Label variants
    {
      variant: "label",
      size: "lg",
      class: "text-sm leading-5 tracking-[0.0001em]", // 14px / 20px / 0.1%
    },
    {
      variant: "label",
      size: "md",
      class: "text-xs 4xl:text-sm leading-4 4xl:leading-5 tracking-[0.005em]", // 12px / 16px / 14px / 20px / +0.5%
    },
    {
      variant: "label",
      size: "sm",
      class: "text-[0.6875rem] leading-4 tracking-[0.005em]", // 11px / 16px / +0.5%
    },
    // Body variants
    {
      variant: "body",
      size: "lg",
      class:
        "text-sm md:text-base 4xl:text-lg leading-5 md:leading-6 4xl:leading-7 tracking-[0.005em]", // 14px / 16px / 24px / 18px / 28px / 0.5%
    },
    {
      variant: "body",
      size: "md",
      class: "text-sm leading-5 tracking-[0.0025em]", // 14px / 20px / +0.25%
    },
    {
      variant: "body",
      size: "sm",
      class: "text-xs leading-4 tracking-[0.004em]", // 12px / 16px / +0.4%
    },
    // Default weights per variant
    {
      variant: "heading",
      weight: undefined,
      class: "font-semibold",
    },
    {
      variant: "title",
      weight: undefined,
      class: "font-semibold",
    },
    {
      variant: "label",
      weight: undefined,
      class: "font-medium",
    },
    {
      variant: "body",
      weight: undefined,
      class: "font-normal",
    },
  ],
  defaultVariants: {
    variant: "body",
    size: "md",
  },
});

type TextOwnProps<T extends ElementType = "p"> = {
  as?: T;
} & VariantProps<typeof textVariants>;

type TextProps<T extends ElementType = "p"> = TextOwnProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof TextOwnProps<T>>;

export default function Text<T extends ElementType = "p">({
  as,
  size,
  weight,
  variant,
  className,
  ...props
}: TextProps<T>) {
  const Component = as || "p";

  return (
    <Component
      className={cn(textVariants({ size, variant, weight }), className)}
      {...props}
    />
  );
}
